import { ISubmitValuationClient } from "../clients";
import {
  EscalateValuationPayload,
  FailedCheckDetails,
  PxQualifications,
  ServiceHistory,
  SubmitValuationPayload,
  ValuationResponse,
} from "../types/apis";
import { AppInputContextState } from "../types/context";
import { FormData } from "../types/formData";
import { hasNoServiceHistory } from "../utils/hasNoServiceHistory";
import { parseFailedChecks } from "../utils/parseFailedChecks";
import { parseJwt } from "../utils/parseJwt";
import { ISubmitValuationAdapter } from ".";

export class SubmitValuationAdapter implements ISubmitValuationAdapter {
  private client: ISubmitValuationClient;
  private bearerToken: string;
  private userEmailAddress: string;
  constructor(client: ISubmitValuationClient, bearerToken: string) {
    this.client = client;
    this.bearerToken = bearerToken;
    this.userEmailAddress = parseJwt(bearerToken).uem;
  }
  async submitValuation(
    appInput: AppInputContextState,
    valuation: ValuationResponse,
    formValues: FormData,
  ): Promise<string> {
    const pxQualifications = this.buildPxQualifications(formValues);

    const submitValuationPayload: SubmitValuationPayload = {
      condition: formValues.condition!,
      costs: valuation.costs,
      depreciation: valuation?.depreciation,
      emailAddress: this.userEmailAddress,
      forecastedExitSalePrice: valuation.forecastedExitSalePrice,
      margin: valuation.margin,
      pxQualifications: pxQualifications,
      qualificationId: parseInt(appInput.qualificationId),
      saleMethod: valuation.saleMethod,
      targetRetailPrice: valuation.targetRetailPrice.toString(),
      valuation: valuation.valuationGbp,
      valuationUuid: valuation.id,
      vrm: appInput.vrm,
    };
    return this.client.submitValuation(submitValuationPayload, this.bearerToken);
  }
  async escalateValuation(
    appInput: AppInputContextState,
    reason: string,
    details: string,
    formValues: FormData,
    checks: FailedCheckDetails[],
    valuation?: ValuationResponse,
  ): Promise<string> {
    const pxQualifications = this.buildPxQualifications(formValues);

    const morePxQualifications = parseFailedChecks(checks);

    const escalateValuationPayload: EscalateValuationPayload = {
      condition: formValues.condition,
      costs: valuation?.costs,
      depreciation: valuation?.depreciation,
      emailAddress: this.userEmailAddress,
      forecastedExitSalePrice: valuation?.forecastedExitSalePrice,
      margin: valuation?.margin,
      pxQualifications: { ...pxQualifications, ...morePxQualifications },
      qualificationId: parseInt(appInput.qualificationId),
      saleMethod: valuation?.saleMethod,
      targetRetailPrice: valuation?.targetRetailPrice.toString(),
      valuation: valuation?.valuationGbp,
      valuationUuid: valuation?.id,
      vrm: appInput.vrm,
      reason: reason,
      details: details,
    };
    return this.client.escalateValuation(escalateValuationPayload, appInput.jwt);
  }
  private parseStringToNumber(field: string | undefined): number | undefined {
    const parsedNumber = field ? parseInt(field) : NaN;
    return isNaN(parsedNumber) ? undefined : parsedNumber;
  }
  private buildPxQualifications(formValues: FormData): PxQualifications {
    const shouldClearServiceHistoryDetails = hasNoServiceHistory(
      formValues.serviceHistory,
    );

    function mapServiceHistoryToCarsaNet(
      serviceHistory: ServiceHistory | undefined,
    ): string | undefined {
      if (serviceHistory === undefined) {
        return undefined;
      }
      const serviceHistoryMap: Record<ServiceHistory, string> = {
        full: "FSH",
        fullIndependent: "FISH",
        some: "PSH",
        none: "None",
        notDue: "ND",
      };

      return serviceHistoryMap[serviceHistory];
    }

    return {
      service_history: mapServiceHistoryToCarsaNet(formValues.serviceHistory),
      date_of_last_service: shouldClearServiceHistoryDetails
        ? undefined
        : formValues.date_of_last_service,
      key: this.parseStringToNumber(formValues.key),
      known_faults: formValues.known_faults,
      left_handed: this.parseStringToNumber(formValues.left_handed),
      log_book: this.parseStringToNumber(formValues.log_book),
      plate_transfer_pending: this.parseStringToNumber(
        formValues.plate_transfer_pending,
      ),
      speedo_change: this.parseStringToNumber(formValues.speedo_change),
      was_taxi: this.parseStringToNumber(formValues.was_taxi),
    };
  }
}
